import React, { useState } from "react";
import api from "@/lib/api";
import { Calendar, Check, Clock, X } from "lucide-react";

const STATUS = {
    pending: { label: "Da confermare", cls: "bg-amber-900/30 text-amber-300 border-amber-700/40" },
    confirmed: { label: "Confermata", cls: "bg-green-900/30 text-green-300 border-green-700/40" },
    reschedule_requested: { label: "Modifica richiesta", cls: "bg-blue-900/30 text-blue-300 border-blue-700/40" },
    cancelled: { label: "Annullata", cls: "bg-ink-900 text-ink-500 border-white/10" },
    declined: { label: "Rifiutata", cls: "bg-ink-900 text-ink-500 border-white/10" },
};

const fmtDT = (iso) => {
    try {
        return new Date(iso).toLocaleString("it-IT", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
};

export default function PortalVisits({ token, appointments, onChanged }) {
    if (appointments.length === 0) return <p className="text-xs text-ink-500">Nessuna visita programmata al momento.</p>;
    return (
        <div className="space-y-3">
            {appointments.map((a) => <VisitRow key={a.id} token={token} a={a} onChanged={onChanged} />)}
        </div>
    );
}

function VisitRow({ token, a, onChanged }) {
    const [mode, setMode] = useState(null);
    const [message, setMessage] = useState("");
    const [busy, setBusy] = useState(false);
    const st = STATUS[a.status] || { label: a.status, cls: "bg-ink-900 text-ink-400 border-white/10" };
    const isActive = !["cancelled", "declined"].includes(a.status);
    const isPast = a.start_at && new Date(a.start_at) < new Date();

    const act = async (action, msg = "") => {
        setBusy(true);
        try {
            await api.post(`/portal/${token}/appointments/${a.id}/action`, { action, message: msg });
            setMode(null); setMessage("");
            onChanged();
        } catch (e) {
            window.alert(e?.response?.data?.detail || "Operazione non riuscita");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid={`portal-visit-${a.id}`} className="border border-white/10 bg-ink-900/40 p-4">
            <div className="flex items-center gap-3 flex-wrap">
                <Calendar size={16} className="text-gold shrink-0" />
                <div className="flex-1 min-w-[180px]">
                    <div className="text-sm text-ink-50">{a.property_title}</div>
                    <div className="text-[11px] text-ink-400 flex items-center gap-1 mt-0.5"><Clock size={10} /> {fmtDT(a.start_at)}</div>
                </div>
                <span className={`text-[9px] uppercase tracking-wider px-2 py-1 border ${st.cls}`}>{st.label}</span>
            </div>
            {isActive && !isPast && (
                <div className="mt-3 pt-3 border-t border-white/5">
                    {mode === null ? (
                        <div className="flex flex-wrap gap-2">
                            {a.status !== "confirmed" && (
                                <button data-testid={`visit-confirm-${a.id}`} onClick={() => act("confirm")} disabled={busy}
                                    className="flex-1 min-w-[110px] py-2 text-xs border border-green-600/50 text-green-300 hover:bg-green-900/20 flex items-center justify-center gap-1.5">
                                    <Check size={13} /> Conferma
                                </button>
                            )}
                            <button data-testid={`visit-reschedule-${a.id}`} onClick={() => setMode("reschedule")}
                                className="flex-1 min-w-[110px] py-2 text-xs border border-blue-600/50 text-blue-300 hover:bg-blue-900/20 flex items-center justify-center gap-1.5">
                                <Clock size={13} /> Chiedi modifica
                            </button>
                            <button data-testid={`visit-cancel-${a.id}`} onClick={() => setMode("cancel")}
                                className="flex-1 min-w-[110px] py-2 text-xs border border-red-700/40 text-red-300 hover:bg-red-900/20 flex items-center justify-center gap-1.5">
                                <X size={13} /> Annulla
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <textarea data-testid={`visit-message-${a.id}`} value={message} onChange={(e) => setMessage(e.target.value)} rows={2}
                                placeholder={mode === "reschedule" ? "Quando preferiresti? (es. giovedì pomeriggio)" : "Motivo dell'annullamento (opzionale)"}
                                className="w-full bg-transparent border border-white/15 px-3 py-2 text-xs text-ink-50 focus:border-gold outline-none" />
                            <div className="flex gap-2">
                                <button data-testid={`visit-send-${a.id}`} onClick={() => act(mode, message)} disabled={busy}
                                    className="cta-gold flex-1 py-2 text-xs">{busy ? "Invio..." : mode === "reschedule" ? "Invia richiesta" : "Conferma annullamento"}</button>
                                <button onClick={() => setMode(null)} className="cta-outline px-4 py-2 text-xs">Indietro</button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
